import type { SearchEngine } from '~/utils/searchSurfaces'

/**
 * Client-side search engine force for A/B tests (KFT-4115).
 *
 * The A/B tool (Mida today, whatever tomorrow) owns allocation and calls
 * `window.kftSearchEngine.force('typesense' | 'algolia')` for visitors in the
 * variation. The choice is persisted in a short-lived cookie that the app never
 * clears — on expiry the A/B tool re-allocates.
 *
 * The cookie is read on the client only: CloudFront's cache key has no cookies,
 * so SSR always renders `SEARCH_ENGINE` (see docs/infrastructure-cloudfront.md).
 */

export const SEARCH_ENGINE_COOKIE = 'kft_search_engine'

/** Lifetime of a forced engine. The A/B tool re-allocates once it expires. */
export const SEARCH_ENGINE_FORCE_TTL_SECONDS = 30 * 60

export const isSearchEngine = (value: unknown): value is SearchEngine =>
  value === 'algolia' || value === 'typesense'

/** Validated `SEARCH_ENGINE` runtime config value, defaulting to 'algolia'. */
export const resolveDefaultSearchEngine = (value: unknown): SearchEngine =>
  isSearchEngine(value) ? value : 'algolia'

/**
 * Engine the client should switch to after hydration. `?searchEngine=` is a QA
 * override and wins over the A/B cookie, which wins over `SEARCH_ENGINE`.
 * Invalid values at any step are ignored.
 */
export const resolveClientSearchEngine = ({
  defaultEngine,
  forced,
  queryOverride,
}: {
  defaultEngine: SearchEngine
  forced?: unknown
  queryOverride?: unknown
}): SearchEngine => {
  if (isSearchEngine(queryOverride)) return queryOverride
  if (isSearchEngine(forced)) return forced
  return defaultEngine
}

/**
 * Inline <head> stub, emitted before any A/B tool loader, so `force()` works
 * even when the tool runs before the app hydrates. It only writes the cookie —
 * the client plugin reads it and later replaces `force` with the reactive
 * version. Never overwrites an existing `window.kftSearchEngine`.
 */
export const getSearchEngineForceStubScript = (): string =>
  `(function(){var w=window;if(w.kftSearchEngine)return;w.kftSearchEngine={force:function(e){if(e!=='algolia'&&e!=='typesense')return;document.cookie='${SEARCH_ENGINE_COOKIE}='+e+';path=/;max-age=${SEARCH_ENGINE_FORCE_TTL_SECONDS};samesite=lax';}};})();`
